import React, { useState, useEffect, useCallback } from "react";
import "./card.css";

export default function Row({
  teams,
  fixturesData,
  teamIndex,
  numberOfFixtures,
  activeGameweek,
  showOriginalScore,
  showCustomScore,
  diffMode = 'overall',
  isNarrowScreen,
  shouldCompactOpponents,
  isTableRow = false,
}) {
  const [teamFixturesData, setTeamFixturesData] = useState(null);

  // Get difficulty based on diffMode (attack/defense/overall)
  // ATT filter → opponent's defense rating (weak defense = easy to score)
  // DEF filter → opponent's attack rating (weak attack = easy clean sheet)
  const getCustomDiff = useCallback((opponentNumber, isHome) => {
    const opponent = teams[opponentNumber];
    if (!opponent) return 0;
    if (diffMode === 'attack') {
      return isHome ? (opponent.a_def || 5) : (opponent.h_def || 5);
    } else if (diffMode === 'defense') {
      return isHome ? (opponent.a_att || 5) : (opponent.h_att || 5);
    }
    return isHome ? (opponent.a_diff || 0) : (opponent.h_diff || 0);
  }, [teams, diffMode]);

  useEffect(() => {
    function getFixturesForTeam(teamId) {
      if (!fixturesData) return null;

      const teamFixtures = fixturesData.filter(
        (fixture) => fixture.team_h === teamId || fixture.team_a === teamId
      );

      const gameweekFixtures = new Array(numberOfFixtures)
        .fill(null)
        .map(() => []);

      let totalDifficulty = 0;
      let customTotalDifficulty = 0;

      for (let i = 0; i < numberOfFixtures; i++) {
        const gameweek = teamFixtures.filter(
          (fixture) => fixture.event === activeGameweek + i
        );

        const gameweekDifficulty = gameweek.reduce((acc, fixture) => {
          const home = fixture.team_h === teamId;
          const difficulty = home
            ? fixture.team_h_difficulty
            : fixture.team_a_difficulty;

          return acc + difficulty;
        }, 0);

        if (gameweek.length > 0) {
          gameweekFixtures[i] = gameweek.map((fixture) => {
            const opponentNumber =
              fixture.team_h === teamId ? fixture.team_a : fixture.team_h;
            const opponent = teams
              ? fixture.team_h === teamId
                ? teams[fixture.team_a].name
                : teams[fixture.team_h].name
              : "";

            const home = fixture.team_h === teamId;
            const difficulty = home
              ? fixture.team_h_difficulty
              : fixture.team_a_difficulty;

            return {
              opponent,
              opponentNumber,
              home,
              difficulty,
              eventNumber: fixture.event,
            };
          });

          customTotalDifficulty += gameweek.reduce((acc, fixture) => {
            const home = fixture.team_h === teamId;
            const opponentNumber =
              fixture.team_h === teamId ? fixture.team_a : fixture.team_h;
            const difficulty = getCustomDiff(opponentNumber, home);

            return acc + difficulty;
          }, 0);
        } else {
          gameweekFixtures[i].push({
            opponentNumber: 0,
            blank_difficulty: 6,
            eventNumber: activeGameweek + i,
          });

          totalDifficulty += 6;  // Add 6 for each blank gameweek
          customTotalDifficulty += 11; // Add 11 for each blank gameweek
        }

        totalDifficulty += gameweekDifficulty;
      }

      return {
        fixtures: gameweekFixtures,
        totalDifficulty,
        customTotalDifficulty,
      };
    }

    const newFixturesData = getFixturesForTeam(teamIndex);
    setTeamFixturesData(newFixturesData);
  }, [teamIndex, numberOfFixtures, fixturesData, teams, activeGameweek, getCustomDiff]);

  const team = teams[teamIndex];
  const teamName = team ? team.name : "";

  if (isTableRow) {
    return (
      <tr>
        <td className="team-info">
          <span className="team-name">{teamName}</span>
          <br className="page-break"/>
          <img className="team-badge" src={team?.badge} alt={teamName} />
        </td>

        <td className="fdr-column">
          {showOriginalScore && teamFixturesData && (
            <h2 className="fdr-number">
              {teamFixturesData.totalDifficulty}
            </h2>
          )}
          {showCustomScore && teamFixturesData && (
            <h2 className="fdr-number">
              {Number(teamFixturesData.customTotalDifficulty).toFixed(1)}
            </h2>
          )}
        </td>

        {teamFixturesData &&
          teamFixturesData.fixtures.map((gameweek, gameweekIndex) => {
            const gameweekNumber = activeGameweek + gameweekIndex;
            if (gameweekNumber > 38) return null;

            return (
              <td className="fixture-column" key={gameweekIndex}>
                {gameweek.length > 1 ? (
                  <div className="dgw-container">
                    {gameweek.map((fixture, index) => {
                      const diffClass = showOriginalScore
                        ? `difficulty-${fixture.difficulty}`
                        : `custom-difficulty-${Math.round(getCustomDiff(fixture.opponentNumber, fixture.home))}`;
                      const compactClass = shouldCompactOpponents ? 'opponent-name-compact' : '';
                      const badge = fixture.opponentNumber !== null && teams[fixture.opponentNumber]?.badge;
                      return (
                        <div
                          className={`fixture-info dgw-fixture ${diffClass}`}
                          key={index}
                        >
                          <span className="dgw-text">
                            <b className={`opponent-name ${compactClass}`}>{fixture.opponent}</b>{" "}
                            {fixture.opponentNumber !== 0 ? (
                              <span className={compactClass}>
                                {fixture.home ? "(H)" : "(A)"}
                              </span>
                            ) : (
                              <strong>BLANK</strong>
                            )}
                          </span>
                          {badge && (
                            <img
                              className="fixture-badge"
                              src={teams[fixture.opponentNumber]?.badge}
                              alt={fixture.opponent}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : gameweek.length > 0 ? (
                  gameweek.map((fixture, index) => (
                    <div
                      className={`fixture-info ${
                        showOriginalScore
                          ? `difficulty-${fixture.difficulty}`
                          : `custom-difficulty-${Math.round(getCustomDiff(fixture.opponentNumber, fixture.home))}`
                      }`}
                      key={index}
                    >
                      <b className={`opponent-name ${shouldCompactOpponents ? 'opponent-name-compact' : ''}`}>{fixture.opponent}</b>{" "}
                      {fixture.opponentNumber !== 0 ? (
                        <span className={`${shouldCompactOpponents ? 'opponent-name-compact' : ''}`}>
                          {fixture.home ? "(H)" : "(A)"}
                        </span>
                      ) : (
                        <strong>BLANK</strong>
                      )}
                      <br />
                      {fixture.opponentNumber !== null &&
                        teams[fixture.opponentNumber]?.badge && (
                          <img
                            className="fixture-badge"
                            src={teams[fixture.opponentNumber]?.badge}
                            alt={fixture.opponent}
                          />
                        )}
                      <br />
                    </div>
                  ))
                ) : (
                  <div
                    className={`fixture-info ${
                      showOriginalScore ? `difficulty-${5}` : `custom-difficulty-${10}`
                    }`}
                    key={gameweekIndex}
                  >
                    <b>BLANK</b>
                  </div>
                )}
              </td>
            );
          })}
      </tr>
    );
  }

  return (
    <div>
      <table className="fixtures-table with-border">
        <thead className="table-header">
          <tr>
            <th className="team-column">Team</th>
            <th className="fdr-column">FDR</th>
            {teamFixturesData &&
              teamFixturesData.fixtures.map((_, index) => {
                const gameweekNumber = activeGameweek + index;
                return gameweekNumber <= 38 ? (
                  <th key={index}>{`GW ${gameweekNumber}`}</th>
                ) : null;
              })}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="team-info">
              <span className="team-name">{teamName}</span>
              <br className="page-break"/>
              <img className="team-badge" src={team?.badge} alt={teamName} />
            </td>

            <td className="fdr-column">
              {showOriginalScore && teamFixturesData && (
                <h2 className="fdr-number">
                  {teamFixturesData.totalDifficulty}
                </h2>
              )}
              {showCustomScore && teamFixturesData && (
                <h2 className="fdr-number">
                  {Number(teamFixturesData.customTotalDifficulty).toFixed(1)}
                </h2>
              )}
            </td>

            {teamFixturesData &&
              teamFixturesData.fixtures.map((gameweek, gameweekIndex) => {
                const gameweekNumber = activeGameweek + gameweekIndex;
                if (gameweekNumber > 38) return null;

                return (
                  <td className="fixture-column" key={gameweekIndex}>
                    {gameweek.length > 1 ? (
                      <div className="dgw-container">
                        {gameweek.map((fixture, index) => {
                          const diffClass = showOriginalScore
                            ? `difficulty-${fixture.difficulty}`
                            : `custom-difficulty-${Math.round(getCustomDiff(fixture.opponentNumber, fixture.home))}`;
                          const compactClass = shouldCompactOpponents ? 'opponent-name-compact' : '';
                          const badge = fixture.opponentNumber !== null && teams[fixture.opponentNumber]?.badge;
                          return (
                            <div
                              className={`fixture-info dgw-fixture ${diffClass}`}
                              key={index}
                            >
                              <span className="dgw-text">
                                <b className={`opponent-name ${compactClass}`}>{fixture.opponent}</b>{" "}
                                {fixture.opponentNumber !== 0 ? (
                                  <span className={compactClass}>
                                    {fixture.home ? "(H)" : "(A)"}
                                  </span>
                                ) : (
                                  <strong>BLANK</strong>
                                )}
                              </span>
                              {badge && (
                                <img
                                  className="fixture-badge"
                                  src={teams[fixture.opponentNumber]?.badge}
                                  alt={fixture.opponent}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : gameweek.length > 0 ? (
                      gameweek.map((fixture, index) => (
                        <div
                          className={`fixture-info ${
                            showOriginalScore
                              ? `difficulty-${fixture.difficulty}`
                              : `custom-difficulty-${Math.round(getCustomDiff(fixture.opponentNumber, fixture.home))}`
                          }`}
                          key={index}
                        >
                          <b className={`opponent-name ${shouldCompactOpponents ? 'opponent-name-compact' : ''}`}>{fixture.opponent}</b>{" "}
                          {fixture.opponentNumber !== 0 ? (
                            <span className={`${shouldCompactOpponents ? 'opponent-name-compact' : ''}`}>
                              {fixture.home ? "(H)" : "(A)"}
                            </span>
                          ) : (
                            <strong>BLANK</strong>
                          )}
                          <br />
                          {fixture.opponentNumber !== null &&
                            teams[fixture.opponentNumber]?.badge && (
                              <img
                                className="fixture-badge"
                                src={teams[fixture.opponentNumber]?.badge}
                                alt={fixture.opponent}
                              />
                            )}
                          <br />
                        </div>
                      ))
                    ) : (
                      <div
                        className={`fixture-info ${
                          showOriginalScore ? `difficulty-${5}` : `custom-difficulty-${10}`
                        }`}
                        key={gameweekIndex}
                      >
                        <b>BLANK</b>
                      </div>
                    )}
                  </td>
                );
              })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
